import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Clock, User, BookOpen, Star, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getCategoryColors } from '@/lib/category-colors';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Đường dẫn placeholder image khi không có hình ảnh
const PLACEHOLDER_IMAGE = '/images/logo.png';

interface MobileCardProps {
  id: string;
  slug?: string;
  title: string;
  description?: string;
  image?: string;
  icon?: ReactNode;
  color?: 'blue' | 'orange' | 'green' | 'purple' | 'red' | 'yellow';
  duration?: string; // Format: "17 Phút"
  lessons?: number;
  onClick?: () => void;
  footer?: ReactNode;
  progress?: number; // 0-100
  className?: string;
  imagePosition?: 'left' | 'right';
  author?: string;
  compact?: boolean;
  isCompleted?: boolean;
  rating?: number; // 0-5
  level?: 'beginner' | 'intermediate' | 'advanced';
  studentsCount?: number;
  tags?: string[];
  price?: number;
  isFeatured?: boolean;
  category?: string;
  index?: number; // For staggered animation
}

// Map level to text and colors
const levelConfig = {
  beginner: {
    text: 'Cơ bản',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
  },
  intermediate: {
    text: 'Trung cấp',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
  },
  advanced: {
    text: 'Nâng cao',
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  }
} as const;

// Thêm author mặc định
const DEFAULT_AUTHOR = 'Giác Trí Tịnh';

export const MobileCard: React.FC<MobileCardProps> = ({
  id,
  slug,
  title,
  description,
  image,
  icon,
  color = 'blue',
  duration,
  lessons,
  onClick,
  footer,
  progress,
  className,
  imagePosition = 'top',
  author = DEFAULT_AUTHOR, // Set default author
  compact = false,
  isCompleted,
  rating,
  level = 'beginner',
  studentsCount = 0,
  tags = [],
  price = 0,
  isFeatured,
  category,
  index = 0
}) => {
  const levelData = levelConfig[level];
  const categoryStyle = category ? getCategoryColors(category) : null;
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      const courseIdentifier = slug || id;
      navigate(`/courses/${courseIdentifier}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={handleClick}
      className={cn(
        'bg-card rounded-xl overflow-hidden border shadow-sm transition-all duration-300',
        'hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 cursor-pointer',
        className
      )}
    >
      {/* Image Section */}
      <div className="aspect-[16/9] overflow-hidden relative">
        <img 
          src={image || PLACEHOLDER_IMAGE}
          alt={title} 
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error('Image load error:', {
              failedSrc: e.currentTarget.src,
              originalImage: image
            });
            
            if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
              e.currentTarget.src = PLACEHOLDER_IMAGE;
            } else {
              console.error('Placeholder image failed to load');
              e.currentTarget.style.display = 'none';
            }
          }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          {rating !== undefined && rating > 0 && (
            <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center shadow-sm">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 mr-1" />
              <span className="font-medium">{rating.toFixed(1)}</span>
            </div>
          )}
          {isFeatured && (
            <Badge variant="secondary" className="bg-primary/90 text-primary-foreground border-none shadow-sm">
              Nổi bật
            </Badge>
          )}
        </div>

        {/* Category Badge */}
        {category && categoryStyle && (
          <div className="absolute top-2 left-2">
            <Badge 
              variant="outline" 
              className={cn(
                "border-2 shadow-sm font-medium",
                categoryStyle.border,
                categoryStyle.text,
                "bg-white/90 dark:bg-black/90"
              )}
            >
              {category}
            </Badge>
          </div>
        )}
      </div>

      {/* Content Section với padding nhỏ hơn */}
      <div className="pt-2 pb-3 px-4">
        <h3 className="font-bold text-base text-foreground line-clamp-2">{title}</h3>
        {description && (
          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{description}</p>
        )}

        {/* Metadata với spacing nhỏ hơn */}
        <div className="mt-2.5 space-y-2">
          {/* Level & Duration */}
          <div className="flex items-center justify-between text-xs">
            <Badge variant="secondary" className={cn("font-medium shadow-sm", levelData.className)}>
              {levelData.text}
            </Badge>
            {duration && (
              <div className="flex items-center text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5 mr-1" />
                <span>{duration}</span>
              </div>
            )}
          </div>

          {/* Author & Students Count - Luôn hiển thị */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center bg-muted/30 px-2 py-1 rounded-full">
              <User className="w-3.5 h-3.5 mr-1" />
              <span className="line-clamp-1">{author}</span>
            </div>
            <div className="flex items-center bg-muted/30 px-2 py-1 rounded-full">
              <Users className="w-3.5 h-3.5 mr-1" />
              <span>{studentsCount} học viên</span>
            </div>
          </div>

          {/* Progress Bar */}
          {progress !== undefined && (
            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
              <motion.div 
                className="bg-primary h-1.5 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          )}

          {/* Price */}
          {price !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {price === 0 ? 'Miễn phí' : `${price.toLocaleString('vi-VN')}đ`}
              </span>
              {lessons !== undefined && lessons > 0 && (
                <div className="flex items-center text-xs text-muted-foreground bg-muted/30 px-2 py-1 rounded-full">
                  <BookOpen className="w-3.5 h-3.5 mr-1" />
                  <span>{lessons} bài học</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </motion.div>
  );
};

export default MobileCard; 